import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

/**
 * Status-Lämpchen für Anlagen-Meldungen.
 *
 * - PV-Anlage: 3 Lämpchen (Einspeiseanfrage, Marktstammdatenregister, Fertigmeldung)
 * - Wärmepumpe / Klimaanlage: 1 Lämpchen (§14a-Meldung)
 * - Sonstige / unbekannt: nichts
 *
 * Eine Aufgabe gilt als "grün" (erledigt), sobald NUMMER UND DATUM gesetzt sind.
 */
const LAMP_SIZE = {
  compact: "h-2.5 w-2.5",
  large: "h-3.5 w-3.5",
};

const isDone = (number, date) => Boolean(number && date);

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

const Lamp = ({ label, number, date, size = "compact", testId }) => {
  const done = isDone(number, date);
  const color = done ? "bg-emerald-500 ring-emerald-300" : "bg-red-500 ring-red-300";
  const ringClass = "ring-2 ring-offset-1 ring-offset-background";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId}
            data-status={done ? "done" : "open"}
            className={`inline-block rounded-full shadow-sm ${LAMP_SIZE[size]} ${color} ${ringClass} cursor-help`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-medium">{label}</p>
          {done ? (
            <>
              <p>Nr.: {number}</p>
              <p>Datum: {fmtDate(date)}</p>
            </>
          ) : (
            <p className="text-red-200">noch offen</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ProjectStatusLamps = ({ project, size = "compact", showLabel = false, className = "" }) => {
  if (!project) return null;

  // Lämpchen erscheinen erst, sobald die Auftragsbestätigung versendet wurde
  if (!project.order_confirmation_sent_at) {
    return null;
  }

  // Wenn Anlagentyp noch nicht gesetzt ist (z. B. Freitext/Legacy-Angebot): Fallback auf PV-Anzeige,
  // damit der User die 3 roten Lämpchen sofort sieht und in den Stammdaten den Typ ggf. anpassen kann.
  const systemType = project.system_type || "pv";

  if (systemType === "pv") {
    return (
      <div className={`flex items-center gap-1.5 ${className}`} data-testid="project-lamps-pv">
        <Lamp
          label="Einspeiseanfrage"
          number={project.einspeiseanfrage_number}
          date={project.einspeiseanfrage_date}
          size={size}
          testId="lamp-einspeise"
        />
        <Lamp
          label="Marktstammdatenregister"
          number={project.marktstammdaten_number}
          date={project.marktstammdaten_date}
          size={size}
          testId="lamp-mastr"
        />
        <Lamp
          label="Fertigmeldung"
          number={project.fertigmeldung_number}
          date={project.fertigmeldung_date}
          size={size}
          testId="lamp-fertig"
        />
        {showLabel && (
          <span className="ml-1 text-xs text-muted-foreground">PV-Meldungen</span>
        )}
      </div>
    );
  }

  if (systemType === "heatpump" || systemType === "climate") {
    return (
      <div className={`flex items-center gap-1.5 ${className}`} data-testid="project-lamps-14a">
        <Lamp
          label="§14a EnWG-Meldung"
          number={project.paragraph14a_number}
          date={project.paragraph14a_date}
          size={size}
          testId="lamp-14a"
        />
        {showLabel && (
          <span className="ml-1 text-xs text-muted-foreground">§14a-Meldung</span>
        )}
      </div>
    );
  }

  // "other"
  return showLabel ? (
    <span className="text-xs text-muted-foreground italic">keine Meldepflicht</span>
  ) : null;
};

export default ProjectStatusLamps;
